'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AssetPopup } from '@/components/assets-explorer/asset-popup'
import { Asset } from '@/lib/assets/types'
import {
  ASSET_DETAILS_STALE_TIME,
  fetchAssetDetails,
  getAssetDetailsQueryKey,
} from '@/lib/hooks/useAssetDetails'
import { CrateCartAssetOpenDetail } from '@/lib/marketplace/crate-cart'
import { useHeaderFeatures } from '@/lib/contexts/HeaderFeaturesContext'

export function CrateCartAssetOpenListener() {
  const queryClient = useQueryClient()
  const { marketplaceCartEnabled } = useHeaderFeatures()
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const activeRequestRef = useRef<AbortController | null>(null)

  const openAssetFromCart = useCallback(
    async ({ assetUuid, assetHref }: CrateCartAssetOpenDetail) => {
      if (!assetUuid) {
        return
      }

      activeRequestRef.current?.abort()
      const controller = new AbortController()
      activeRequestRef.current = controller

      try {
        const assets = await queryClient.fetchQuery({
          queryKey: getAssetDetailsQueryKey([assetUuid]),
          queryFn: () => fetchAssetDetails([assetUuid], controller.signal),
          staleTime: ASSET_DETAILS_STALE_TIME,
        })
        const asset = assets.find((item) => item.uuid === assetUuid) ?? null

        if (controller.signal.aborted) {
          return
        }

        if (asset?.mainCategorySlug) {
          setSelectedAsset(asset)
          return
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return
        }

        console.error('[marketplace] failed to resolve asset from shared cart', error)
      }

      if (assetHref) {
        window.location.assign(assetHref)
      }
    },
    [queryClient]
  )

  useEffect(() => {
    if (!marketplaceCartEnabled) {
      return
    }

    const handleAssetOpen = (event: Event) => {
      const customEvent = event as CustomEvent<CrateCartAssetOpenDetail>
      const detail = customEvent.detail

      if (detail?.source !== 'marketplace-cart' || !detail.assetUuid) {
        return
      }

      event.preventDefault()
      void openAssetFromCart(detail)
    }

    window.addEventListener('crate:asset:open', handleAssetOpen as EventListener)

    return () => {
      activeRequestRef.current?.abort()
      window.removeEventListener('crate:asset:open', handleAssetOpen as EventListener)
    }
  }, [marketplaceCartEnabled, openAssetFromCart])

  if (!selectedAsset?.mainCategorySlug) {
    return null
  }

  return (
    <AssetPopup
      assetId={selectedAsset.uuid}
      mainCategoryId={selectedAsset.mainCategorySlug}
      disableNavigation
      onOpenChange={(open) => {
        if (!open) {
          setSelectedAsset(null)
        }
      }}
    />
  )
}
